import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UIRouterModule } from '@uirouter/angular';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { ProgressbarModule } from 'ngx-bootstrap/progressbar';
import { TimepickerModule } from 'ngx-bootstrap/timepicker';
import { TimepickerTabIndexDirective } from '../shared/directives/timepicker-tab-index.directive';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

import { SharedModule } from '../shared/shared.module';

import { ScrollingModule } from '@angular/cdk/scrolling';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { DisplayStatusComponent } from '../displays/components/display-status/display-status.component';
import { ContenteditableDirective } from '../shared/directives/content-editable/content-editable.directive';
import { BackgroundImageComponent } from './background-image/background-image.component';
import { BetaBadgeComponent } from './beta-badge/beta-badge.component';
import { CalendarMonthYearHeaderComponent } from './calendar-month-year-header/calendar-month-year-header.component';
import { ChangeDetailsComponent } from './change-details/change-details.component';
import { ColorPickerComponent } from './color-picker/color-picker.component';
import { CommonActionsComponent } from './common-actions/common-actions.component';
import { CompanyIcpModalComponent } from './company-icp/components/company-icp-modal/company-icp-modal.component';
import { CompanyRoleModalComponent } from './company-icp/components/company-role-modal/company-role-modal.component';
import { ContentTitleComponent } from './content-title/content-title.component';
import { DistributionListComponent } from './distribution/distribution-list/distribution-list.component';
import { DistributionModalComponent } from './distribution/distribution-modal/distribution-modal.component';
import { DistributionSelectorComponent } from './distribution/distribution-selector/distribution-selector.component';
import { RvDividerComponent } from './divider/divider.component';
import { EmailsFieldComponent } from './emails-field/emails-field.component';
import { FocusMeDirective } from './focus-me/focus-me.directive';
import { FolderPathComponent } from './folders/folder-path/folder-path.component';
import { InitialsBadgeComponent } from './initials-badge/initials-badge.component';
import { LastModifiedComponent } from './last-modified/last-modified/last-modified.component';
import { LastRevisedComponent } from './last-modified/last-revised/last-revised.component';
import { RvSpinnerDirective } from './loading/rv-spinner.directive';
import { BulkEditModalComponent } from './modals/bulk-edit-modal/bulk-edit-modal.component';
import { ConfirmModalComponent } from './modals/confirm-modal/confirm-modal.component';
import { DialogHeaderComponent } from './modals/dialog-header/dialog-header.component';
import { FirmwareUpdateModalComponent } from './modals/firmware-update-modal/firmware-update-modal.component';
import { MessageBoxComponent } from './modals/message-box/message-box.component';
import { OtaUpdateModalComponent } from './modals/ota-update-modal/ota-update-modal.component';
import { SafeDeleteModalComponent } from './modals/safe-delete-modal/safe-delete-modal.component';
import { NumberInputComponent } from './number-input/number-input.component';
import { PasswordInputComponent } from './password-input/password-input.component';
import { UnlockFeatureComponent } from './plans/unlock-feature/unlock-feature.component';
import { UpgradeFeatureModalComponent } from './plans/upgrade-feature-modal/upgrade-feature-modal.component';
import { UpgradeNoticeComponent } from './plans/upgrade-notice/upgrade-notice.component';
import { UpgradePlanComponent } from './plans/upgrade-plan/upgrade-plan.component';
import { BatchActionsComponent } from './scrolling-list/batch-actions/batch-actions.component';
import { BatchOperationsComponent } from './scrolling-list/batch-operations/batch-operations.component';
import { BulkDeleteConfirmationComponent } from './scrolling-list/bulk-delete-confirmation/bulk-delete-confirmation.component';
import { CompanyIdModalComponent } from './scrolling-list/company-id-modal/company-id-modal.component';
import { ScrollingListDirective } from './scrolling-list/scrolling-list.directive';
import { SearchFilterComponent } from './search-filter/search-filter.component';
import { RvBadgeComponent } from './rv-badge/rv-badge.component';
import { StreamlineIconComponent } from './streamline-icon/streamline-icon.component';
import { StretchyInputComponent } from './stretchy-input/stretchy-input.component';
import { SuggestGeneralDeliveryComponent } from './suggest-general-delivery/suggest-general-delivery.component';
import { SvgIconComponent } from './svg-icon/svg-icon.component';
import { PopupTimePickerComponent } from './time-date/popup-time-picker/popup-time-picker.component';
import { TimeInputComponent } from './time-input/time-input.component';
import { DateEndAfterStartDirective } from './timeline/date-end-after-start.directive';
import { MonthDropdownComponent } from './timeline/month-dropdown/month-dropdown.component';
import { PickTimeRangeComponent } from './timeline/pick-time-range/pick-time-range.component';
import { TimelineBasicModalComponent } from './timeline/timeline-basic-modal/timeline-basic-modal.component';
import { TimelineBasicTextboxComponent } from './timeline/timeline-basic-textbox/timeline-basic-textbox.component';
import { TimelineModalComponent } from './timeline/timeline-modal/timeline-modal.component';
import { TimelineTextboxComponent } from './timeline/timeline-textbox/timeline-textbox.component';
import { WeekDropdownComponent } from './timeline/week-dropdown/week-dropdown.component';
import { WeekdayCheckboxesComponent } from './timeline/weekday-checkboxes/weekday-checkboxes.component';
import { WeekdayDropdownComponent } from './timeline/weekday-dropdown/weekday-dropdown.component';
import { UrlFieldComponent } from './url-field/url-field.component';
import { UrlFileTypeValidatorDirective } from './url-field/validators/file-type-validator.directive';
import { HttpValidatorDirective } from './url-field/validators/http-validator.directive';
import { UrlPatternValidatorDirective } from './url-field/validators/url-pattern-validator.directive';
import { ActionLinkDirective } from '../accessibility/directives/action-link/action-link.directive';

@NgModule({
  declarations: [
    MessageBoxComponent,
    ColorPickerComponent,
    ConfirmModalComponent,
    LastModifiedComponent,
    LastRevisedComponent,
    StreamlineIconComponent,
    StretchyInputComponent,
    PopupTimePickerComponent,
    ScrollingListDirective,
    BatchOperationsComponent,
    TimelineTextboxComponent,
    SuggestGeneralDeliveryComponent,
    EmailsFieldComponent,
    PasswordInputComponent,
    SvgIconComponent,
    BackgroundImageComponent,
    DistributionSelectorComponent,
    BetaBadgeComponent,
    CompanyIdModalComponent,
    BulkDeleteConfirmationComponent,
    DistributionListComponent,
    DistributionModalComponent,
    TimelineModalComponent,
    MonthDropdownComponent,
    WeekdayDropdownComponent,
    WeekDropdownComponent,
    DateEndAfterStartDirective,
    UnlockFeatureComponent,
    UpgradePlanComponent,
    TimelineBasicModalComponent,
    PickTimeRangeComponent,
    WeekdayCheckboxesComponent,
    TimelineBasicTextboxComponent,
    CalendarMonthYearHeaderComponent,
    BulkEditModalComponent,
    FirmwareUpdateModalComponent,
    OtaUpdateModalComponent,
    NumberInputComponent,
    UpgradeNoticeComponent,
    UpgradeFeatureModalComponent,
    ContentTitleComponent,
    ChangeDetailsComponent,
    CommonActionsComponent,
    SafeDeleteModalComponent,
    InitialsBadgeComponent,
    CompanyRoleModalComponent,
    CompanyIcpModalComponent,
    BatchActionsComponent,
  ],
  imports: [
    ActionLinkDirective,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ProgressbarModule,
    TooltipModule,
    TimepickerModule,
    TimepickerTabIndexDirective,
    PopoverModule,
    SharedModule,
    UIRouterModule,
    MatButtonModule,
    MatTooltipModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatRadioModule,
    MatSlideToggleModule,
    MatChipsModule,
    MatDatepickerModule,
    MatIconModule,
    MatNativeDateModule,
    UrlFieldComponent,
    UrlPatternValidatorDirective,
    HttpValidatorDirective,
    UrlFileTypeValidatorDirective,
    MatMenuModule,
    MatDialogModule,
    ContenteditableDirective,
    SearchFilterComponent,
    RvBadgeComponent,
    FocusMeDirective,
    TimeInputComponent,
    DisplayStatusComponent,
    RvSpinnerDirective,
    FolderPathComponent,
    DialogHeaderComponent,
    ScrollingModule,
    RvDividerComponent,
  ],
  exports: [
    ColorPickerComponent,
    LastModifiedComponent,
    LastRevisedComponent,
    StreamlineIconComponent,
    StretchyInputComponent,
    RvSpinnerDirective,
    PopupTimePickerComponent,
    SearchFilterComponent,
    ScrollingListDirective,
    BatchOperationsComponent,
    FocusMeDirective,
    TimelineTextboxComponent,
    SuggestGeneralDeliveryComponent,
    EmailsFieldComponent,
    PasswordInputComponent,
    SvgIconComponent,
    BackgroundImageComponent,
    DistributionSelectorComponent,
    BetaBadgeComponent,
    DistributionSelectorComponent,
    TimelineBasicTextboxComponent,
    NumberInputComponent,
    UpgradeNoticeComponent,
    ContentTitleComponent,
    ChangeDetailsComponent,
    CommonActionsComponent,
    TimeInputComponent,
    SafeDeleteModalComponent,
    InitialsBadgeComponent,
    BatchActionsComponent,
    RvDividerComponent,
    TimelineModalComponent,
  ],
})
export class ComponentsModule {}
